// src/Components/Broadcast/components/HistoryPanel.js
import React, { useState } from 'react';
import { Clock, Trash2, ImageIcon, ChevronDown, ChevronUp, CheckCircle, XCircle, Users, Mail, Eye, Copy, Download, Filter, Calendar, Target, Gift, BookOpen, Crown, RefreshCw } from 'lucide-react';

export const HistoryPanel = ({ 
  showHistory, 
  broadcastHistory, 
  formatDate, 
  clearHistory, 
  onResendToFailed, 
  onExcludeSentUsers, 
  onDeleteItem,
  onResendSelectedToFailed
}) => {
  const [expandedItem, setExpandedItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showTooltip, setShowTooltip] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);

  if (!showHistory) return null;

  const toggleExpand = (id) => {
    setExpandedItem(expandedItem === id ? null : id);
  };

  const toggleSelectItem = (itemId, e) => {
    e.stopPropagation();
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const selectAllItems = () => {
    if (selectedItems.length === broadcastHistory.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(broadcastHistory.map(item => item.id));
    }
  };

  const handleResendToFailed = (item) => {
    if (onResendToFailed && item.failedUsers && item.failedUsers.length > 0) {
      onResendToFailed(item.failedUsers.map(u => u.id));
    }
  };

  const handleResendSelectedToFailed = () => {
    if (onResendSelectedToFailed && selectedItems.length > 0) {
      const allFailedUsers = [];
      broadcastHistory.forEach(item => {
        if (selectedItems.includes(item.id) && item.failedUsers && item.failedUsers.length > 0) {
          item.failedUsers.forEach(fail => {
            allFailedUsers.push(fail.id);
          });
        }
      });
      if (allFailedUsers.length > 0) {
        onResendSelectedToFailed(allFailedUsers);
      }
    }
  };

  const handleExcludeSentUsers = (item) => {
    if (onExcludeSentUsers && item.successUsers && item.successUsers.length > 0) {
      onExcludeSentUsers(item.successUsers);
    }
  };

  const handleDeleteItem = (itemId, e) => {
    e.stopPropagation();
    if (onDeleteItem) {
      onDeleteItem(itemId);
    }
  };

  const getCombinationLabel = (type) => {
    const labels = {
      'has_products': 'Есть продукты',
      'no_products': 'Нет продуктов',
      'has_gifts_no_products': 'Подарок без продуктов',
      'has_gifts_and_products': 'Подарок + продукты',
      'has_gifts_no_premium': 'Подарок без Premium',
      'no_products_any': 'Нет ни одного продукта'
    };
    return labels[type] || 'Стандартный';
  };

  const getCombinationIcon = (type) => {
    const icons = {
      'has_products': <BookOpen className="w-3 h-3" />,
      'no_products': <BookOpen className="w-3 h-3" />,
      'has_gifts_no_products': <Gift className="w-3 h-3" />,
      'has_gifts_and_products': <div className="flex gap-0.5"><Gift className="w-2.5 h-2.5" /><BookOpen className="w-2.5 h-2.5" /></div>,
      'has_gifts_no_premium': <div className="flex gap-0.5"><Gift className="w-2.5 h-2.5" /><Crown className="w-2.5 h-2.5 text-red-400" /></div>,
      'no_products_any': <Target className="w-3 h-3" />
    };
    return icons[type] || <Filter className="w-3 h-3" />;
  };

  const getTargetFilterLabel = (filter) => {
    const labels = {
      'all': 'Все',
      'premium': 'Premium',
      'free': 'Free',
      'selected': 'Выбранные'
    };
    return labels[filter] || filter;
  };

  const getStatusColor = (item) => {
    if (item.failedCount === 0) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (item.successCount === 0) return 'bg-red-500/20 text-red-400 border-red-500/30';
    return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
  };

  const getStatusIcon = (item) => {
    if (item.failedCount === 0) return <CheckCircle className="w-3 h-3" />;
    if (item.successCount === 0) return <XCircle className="w-3 h-3" />;
    return <Clock className="w-3 h-3" />;
  };

  const handleCopyUserId = (userId, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(userId);
    setShowTooltip(userId);
    setTimeout(() => setShowTooltip(null), 1500);
  };

  const getSelectedStats = () => {
    let totalFailed = 0;
    let totalSuccess = 0;
    broadcastHistory.forEach(item => {
      if (selectedItems.includes(item.id)) {
        totalFailed += item.failedCount || 0;
        totalSuccess += item.successCount || 0;
      }
    });
    return { totalFailed, totalSuccess };
  };

  const selectedStats = getSelectedStats();

  return (
    <div className="mb-4 bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      {/* Заголовок */}
      <div className="px-4 py-3 border-b border-gray-700 bg-gray-800/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-medium text-white">История рассылок</h3>
          <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded-full">
            {broadcastHistory.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {selectedItems.length > 0 && (
            <button
              onClick={handleResendSelectedToFailed}
              className="p-1 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/30 rounded transition-colors"
              title={`Повторить отправку для ${selectedStats.totalFailed} получателей с ошибками`}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
          {broadcastHistory.length > 0 && (
            <button
              onClick={clearHistory}
              className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-900/30 rounded transition-colors"
              title="Очистить всю историю"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Список записей */}
      <div className="max-h-80 overflow-y-auto">
        {broadcastHistory.length === 0 ? (
          <div className="py-8 text-center">
            <Mail className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">История рассылок пуста</p>
            <p className="text-xs text-gray-600 mt-1">Отправьте первое сообщение, чтобы увидеть историю</p>
          </div>
        ) : (
          <>
            {/* Кнопка выбора всех */}
            <div className="px-4 py-2 border-b border-gray-700 flex items-center justify-between bg-gray-800/30">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedItems.length === broadcastHistory.length && broadcastHistory.length > 0}
                  onChange={selectAllItems}
                  className="rounded bg-gray-700 border-gray-600"
                />
                <span className="text-xs text-gray-400">Выбрать все</span>
              </label>
              {selectedItems.length > 0 && (
                <div className="text-xs text-gray-400">
                  Выбрано: {selectedItems.length} | 
                  Успешно: {selectedStats.totalSuccess} | 
                  Ошибки: {selectedStats.totalFailed}
                </div>
              )}
            </div>

            {broadcastHistory.map((item) => (
              <div
                key={item.id}
                className={`border-b border-gray-700 transition-all ${
                  hoveredItem === item.id ? 'bg-gray-700/30' : ''
                } ${selectedItems.includes(item.id) ? 'bg-blue-900/20' : ''}`}
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                {/* Основная строка - компактная */}
                <div
                  className="px-4 py-2 cursor-pointer hover:bg-gray-700/20 transition-colors"
                  onClick={() => toggleExpand(item.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    {/* Чекбокс выбора */}
                    <div onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={(e) => toggleSelectItem(item.id, e)}
                        className="rounded bg-gray-700 border-gray-600"
                      />
                    </div>

                    {/* Левая часть - иконка и статус */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${getStatusColor(item)} border`}>
                        {getStatusIcon(item)}
                      </div>
                      <div className="flex items-center gap-1 text-xs">
                        <span className="text-gray-400">{formatDate(item.date)}</span>
                      </div>
                    </div>

                    {/* Текст сообщения */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-300 truncate">
                        {item.text || '(без текста)'}
                      </p>
                    </div>

                    {/* Правая часть - статистика и действия */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Иконка изображения */}
                      {item.hasImage && (
                        <ImageIcon className="w-3 h-3 text-purple-400" title="С изображением" />
                      )}
                      
                      {/* Статистика */}
                      <div className="flex items-center gap-1 text-xs">
                        <span className="text-green-400">{item.successCount}</span>
                        <span className="text-gray-600">/</span>
                        <span className="text-gray-400">{item.totalRecipients}</span>
                      </div>

                      {/* Кнопка повторной отправки ошибочных */}
                      {item.failedCount > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResendToFailed(item);
                          }}
                          className="p-0.5 text-yellow-500 hover:text-yellow-400 rounded transition-colors"
                          title={`Повторить отправку для ${item.failedCount} получателей с ошибками`}
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                      )}

                      {/* Кнопка удаления записи */}
                      {hoveredItem === item.id && (
                        <button
                          onClick={(e) => handleDeleteItem(item.id, e)}
                          className="p-0.5 text-gray-500 hover:text-red-400 rounded transition-colors"
                          title="Удалить запись"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      
                      {/* Иконка разворачивания */}
                      <button className="p-0.5 text-gray-500">
                        {expandedItem === item.id ? (
                          <ChevronUp className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Вторая строка - фильтры (компактная) */}
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 ml-6">
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      <span>{getTargetFilterLabel(item.targetFilter)}</span>
                    </div>
                    
                    {item.combinationType && item.combinationType !== 'has_products' && (
                      <div className="flex items-center gap-1">
                        {getCombinationIcon(item.combinationType)}
                        <span>{getCombinationLabel(item.combinationType)}</span>
                      </div>
                    )}
                    
                    {item.productFilter && (item.productFilter.courses?.length > 0 || item.productFilter.trainings?.length > 0) && (
                      <div className="flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        <span>
                          {item.productFilter.courses?.length || 0} курсов, 
                          {item.productFilter.trainings?.length || 0} тренингов
                        </span>
                      </div>
                    )}
                    
                    {item.giftFilter && item.giftFilter.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Gift className="w-3 h-3" />
                        <span>{item.giftFilter.length} подарков</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Развернутая информация */}
                {expandedItem === item.id && (
                  <div className="px-4 py-3 border-t border-gray-700 bg-gray-800/50 space-y-3">
                    {/* Фильтры подробно */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="bg-gray-900/50 rounded-lg p-2">
                        <div className="flex items-center gap-1 text-gray-400 mb-1">
                          <Filter className="w-3 h-3" />
                          <span>Параметры фильтрации</span>
                        </div>
                        <div className="space-y-1">
                          <div>Тип: {getTargetFilterLabel(item.targetFilter)}</div>
                          <div>Условие: {getCombinationLabel(item.combinationType)}</div>
                          {item.productFilter && (item.productFilter.courses?.length > 0 || item.productFilter.trainings?.length > 0) && (
                            <div>Продукты: {item.productFilter.courses?.length} курсов, {item.productFilter.trainings?.length} тренингов</div>
                          )}
                          {item.giftFilter && item.giftFilter.length > 0 && (
                            <div>Подарки: {item.giftFilter.length} шт.</div>
                          )}
                        </div>
                      </div>
                      
                      <div className="bg-gray-900/50 rounded-lg p-2">
                        <div className="flex items-center gap-1 text-gray-400 mb-1">
                          <Calendar className="w-3 h-3" />
                          <span>Дата и время</span>
                        </div>
                        <div className="space-y-1">
                          <div>{formatDate(item.date)}</div>
                          <div className="text-gray-500">ID: {item.id}</div>
                        </div>
                      </div>
                    </div>

                    {/* Успешные отправки */}
                    {item.successUsers && item.successUsers.length > 0 && (
                      <div className="bg-green-900/10 rounded-lg border border-green-900/30 overflow-hidden">
                        <div className="px-3 py-2 bg-green-900/20 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            <span className="text-xs font-medium text-green-300">
                              Успешно доставлено ({item.successCount})
                            </span>
                          </div>
                          <button
                            onClick={() => handleExcludeSentUsers(item)}
                            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            Исключить всех
                          </button>
                        </div>
                        <div className="p-2 max-h-32 overflow-y-auto">
                          <div className="grid grid-cols-3 gap-1">
                            {item.successUsers.slice(0, 30).map((userId, i) => (
                              <div
                                key={i}
                                className="relative group flex items-center justify-between text-xs text-gray-400 font-mono bg-gray-800/50 px-2 py-1 rounded"
                              >
                                <span className="truncate">{userId}</span>
                                <button
                                  onClick={(e) => handleCopyUserId(userId, e)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                                >
                                  <Copy className="w-2.5 h-2.5 text-gray-500 hover:text-gray-300" />
                                </button>
                                {showTooltip === userId && (
                                  <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap z-10">
                                    Скопировано
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                          {item.successUsers.length > 30 && (
                            <div className="text-center text-xs text-gray-500 mt-2">
                              и еще {item.successUsers.length - 30} пользователей
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Ошибки доставки */}
                    {item.failedUsers && item.failedUsers.length > 0 && (
                      <div className="bg-red-900/10 rounded-lg border border-red-900/30 overflow-hidden">
                        <div className="px-3 py-2 bg-red-900/20 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <XCircle className="w-3 h-3 text-red-400" />
                            <span className="text-xs font-medium text-red-300">
                              Ошибки доставки ({item.failedCount})
                            </span>
                          </div>
                          <button
                            onClick={() => handleResendToFailed(item)}
                            className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors flex items-center gap-1"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Повторить отправку
                          </button>
                        </div>
                        <div className="p-2 max-h-32 overflow-y-auto">
                          <div className="space-y-1">
                            {item.failedUsers.map((fail, i) => (
                              <div key={i} className="text-xs text-gray-400 font-mono bg-gray-800/50 px-2 py-1 rounded flex items-center justify-between">
                                <span className="truncate">{fail.id}</span>
                                <span className="text-red-400 text-xs ml-2 truncate max-w-[200px]" title={fail.reason}>
                                  {fail.reason?.substring(0, 40)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Дополнительная информация */}
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        onClick={() => handleExcludeSentUsers(item)}
                        className="text-xs text-gray-500 hover:text-blue-400 transition-colors flex items-center gap-1"
                      >
                        <Users className="w-3 h-3" />
                        Исключить успешных
                      </button>
                      {item.failedCount > 0 && (
                        <button
                          onClick={() => handleResendToFailed(item)}
                          className="text-xs text-gray-500 hover:text-yellow-400 transition-colors flex items-center gap-1"
                        >
                          <RefreshCw className="w-3 h-3" />
                          Повторить ошибки
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};